export const CANTO_URL_PATTERN = /media\.zuerich\.com/
export const CANTO_VIDEO_BASE_URL =
  'https://media.zuerich.com/asset/727218228053'

export const CANTO_CROP_ALIGNMENTS = [
  'tl',
  'tc',
  'tr',
  'ml',
  'mc',
  'mr',
  'bl',
  'bc',
  'br',
] as const

export type CantoCropAlignment = (typeof CANTO_CROP_ALIGNMENTS)[number]

export const DEFAULT_CANTO_CROP_ALIGNMENT: CantoCropAlignment = 'mc'

export function resolveCantoCropAlignment(value: unknown): CantoCropAlignment {
  if (
    typeof value === 'string' &&
    (CANTO_CROP_ALIGNMENTS as readonly string[]).includes(value)
  ) {
    return value as CantoCropAlignment
  }
  return DEFAULT_CANTO_CROP_ALIGNMENT
}

export const CANTO_CROP_ALIGNMENT_FOCAL_POINT: Record<
  CantoCropAlignment,
  { x: number; y: number }
> = {
  tl: { x: 0, y: 0 },
  tc: { x: 50, y: 0 },
  tr: { x: 100, y: 0 },
  ml: { x: 0, y: 50 },
  mc: { x: 50, y: 50 },
  mr: { x: 100, y: 50 },
  bl: { x: 0, y: 100 },
  bc: { x: 50, y: 100 },
  br: { x: 100, y: 100 },
}

export function cantoCropAlignmentObjectPosition(value: unknown): string {
  const { x, y } =
    CANTO_CROP_ALIGNMENT_FOCAL_POINT[resolveCantoCropAlignment(value)]
  return `${x}% ${y}%`
}

type CantoImageStyle = {
  url: string
  width: string | undefined
  height: string | undefined
}

type CantoImageStyleOptions = {
  imageStyle?: string
  width?: number
  height?: number
  orientation?: CantoCropAlignment
  /**
   * `crop` (default): smart-crop to exact width×height.
   * `scale`: fit within width×height while keeping the original aspect ratio.
   */
  fit?: 'crop' | 'scale'
}

export function getCantoImageStyle(
  assetId: string,
  options: CantoImageStyleOptions = {},
  renderUrl?: string,
): CantoImageStyle {
  // Strip any existing format/operations suffix (e.g. "/-FWEBP") from the render URL.
  // If renderUrl is provided, use it, otherwise use assetId.
  const baseUrl = renderUrl
    ? renderUrl.replace(/\/-.*$/, '')
    : assetId.replace(/\/-.*$/, '')

  const width = options.width || options.imageStyle?.split('_')[0]
  const height = options.height || options.imageStyle?.split('_')[1]
  const orientation = resolveCantoCropAlignment(options.orientation)
  const fit = options.fit || 'crop'

  const sizeOperation =
    fit === 'scale'
      ? `-S${width}x${height}`
      : `-Ro:${orientation},w:${width},h:${height},n:default`

  return {
    url: `${baseUrl}/-FWEBP,Q85${sizeOperation}`,
    width: width?.toString(),
    height: height?.toString(),
  }
}
